/**
 * Utility functions for floating edge calculations
 * @module react-flow/edges/edge-utils
 */

import { Position, type XYPosition } from '@xyflow/react';

// Type to represent any node-like object (Node or InternalNode)
// Exported so it can be used by other edge components
export interface NodeLike {
  position: XYPosition;
  width?: number | null;
  height?: number | null;
  // InternalNode uses 'internals.positionAbsolute' but we use position
  internals?: {
    positionAbsolute?: XYPosition;
  };
  measured?: {
    width?: number;
    height?: number;
  };
}

/**
 * Get the center point of a node (works with both Node and InternalNode)
 */
export function getNodeCenter(node: NodeLike): XYPosition {
  // Use measured dimensions if available (v12 InternalNode), fallback to width/height
  const width = node.measured?.width ?? node.width ?? 0;
  const height = node.measured?.height ?? node.height ?? 0;
  // Use positionAbsolute if available (v12 InternalNode), fallback to position
  const position = node.internals?.positionAbsolute ?? node.position;

  return {
    x: position.x + width / 2,
    y: position.y + height / 2,
  };
}

/**
 * Calculate the intersection point between a line and a node rectangle.
 * Used for floating edges to determine where the edge should connect.
 */
export function getNodeIntersection(
  intersectionNode: NodeLike,
  targetNode: NodeLike
): XYPosition {
  // Use measured dimensions if available (v12 InternalNode)
  const intersectionNodeWidth = intersectionNode.measured?.width ?? intersectionNode.width ?? 0;
  const intersectionNodeHeight = intersectionNode.measured?.height ?? intersectionNode.height ?? 0;
  // Use positionAbsolute if available (v12 InternalNode)
  const intersectionNodePosition = intersectionNode.internals?.positionAbsolute ?? intersectionNode.position;

  const targetPosition = getNodeCenter(targetNode);

  const w = intersectionNodeWidth / 2;
  const h = intersectionNodeHeight / 2;

  const x2 = intersectionNodePosition.x + w;
  const y2 = intersectionNodePosition.y + h;
  const x1 = targetPosition.x;
  const y1 = targetPosition.y;

  const xx1 = (x1 - x2) / (2 * w) - (y1 - y2) / (2 * h);
  const yy1 = (x1 - x2) / (2 * w) + (y1 - y2) / (2 * h);
  const a = 1 / (Math.abs(xx1) + Math.abs(yy1));
  const xx3 = a * xx1;
  const yy3 = a * yy1;
  const x = w * (xx3 + yy3) + x2;
  const y = h * (-xx3 + yy3) + y2;

  return { x, y };
}

/**
 * Determine the best position (handle side) for an edge endpoint
 */
export function getEdgePosition(
  node: NodeLike,
  intersectionPoint: XYPosition
): Position {
  const nodeCenter = getNodeCenter(node);
  const dx = intersectionPoint.x - nodeCenter.x;
  const dy = intersectionPoint.y - nodeCenter.y;

  // Determine which side the intersection is closest to
  const absX = Math.abs(dx);
  const absY = Math.abs(dy);

  if (absX > absY) {
    return dx > 0 ? Position.Right : Position.Left;
  } else {
    return dy > 0 ? Position.Bottom : Position.Top;
  }
}

/**
 * Get edge parameters for floating edge, including source/target positions
 * and the actual intersection points on both nodes
 */
export function getEdgeParams(
  source: NodeLike,
  target: NodeLike
): {
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  sourcePosition: Position;
  targetPosition: Position;
} {
  const sourceIntersectionPoint = getNodeIntersection(source, target);
  const targetIntersectionPoint = getNodeIntersection(target, source);

  const sourcePosition = getEdgePosition(source, sourceIntersectionPoint);
  const targetPosition = getEdgePosition(target, targetIntersectionPoint);

  return {
    sourceX: sourceIntersectionPoint.x,
    sourceY: sourceIntersectionPoint.y,
    targetX: targetIntersectionPoint.x,
    targetY: targetIntersectionPoint.y,
    sourcePosition,
    targetPosition,
  };
}

/**
 * Calculate the path for a smooth bezier curve between two points
 */
export function getSmoothStepPath(
  sourceX: number,
  sourceY: number,
  sourcePosition: Position,
  targetX: number,
  targetY: number,
  targetPosition: Position
): string {
  const offset = 20;

  // Calculate control points based on positions
  let sourceControlX = sourceX;
  let sourceControlY = sourceY;
  let targetControlX = targetX;
  let targetControlY = targetY;

  switch (sourcePosition) {
    case Position.Left:
      sourceControlX = sourceX - offset;
      break;
    case Position.Right:
      sourceControlX = sourceX + offset;
      break;
    case Position.Top:
      sourceControlY = sourceY - offset;
      break;
    case Position.Bottom:
      sourceControlY = sourceY + offset;
      break;
  }

  switch (targetPosition) {
    case Position.Left:
      targetControlX = targetX - offset;
      break;
    case Position.Right:
      targetControlX = targetX + offset;
      break;
    case Position.Top:
      targetControlY = targetY - offset;
      break;
    case Position.Bottom:
      targetControlY = targetY + offset;
      break;
  }

  return `M ${sourceX},${sourceY} C ${sourceControlX},${sourceControlY} ${targetControlX},${targetControlY} ${targetX},${targetY}`;
}

/**
 * Get a straight line path between two points
 */
export function getStraightPath(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number
): string {
  return `M ${sourceX},${sourceY} L ${targetX},${targetY}`;
}
